import { CreativeAPIs, TemplateAPIs } from '@/api';
import type { TemplateDetails, TemplateListItem } from '@/api/types/template';
import type { TemplateVariant } from '@/api/types/variants';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { Modal } from '@/common/modal';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState, useCallback, type FC } from 'react';
import { SizeTile } from './size-tile';

interface SelectSizeModalProps {
    onDone?: (creativeId: number) => void;
    templateId: number;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    creativeId?: number;
    initiallySelectedSizes?: number[];
}

export const SelectSizeModal: FC<SelectSizeModalProps> = ({
    onDone,
    isOpen,
    setIsOpen,
    templateId,
    creativeId,
    initiallySelectedSizes,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [templateDetails, setTemplateDetails] = useState<TemplateDetails | null>(null);
    const [selectedSizes, setSelectedSizes] = useState<number[]>(initiallySelectedSizes ?? []);
    const [removedSizes, setRemovedSizes] = useState<number[]>([]);

    const fetchTemplateDetails = useCallback(async () => {
        setIsLoading(true);
        try {
            setTemplateDetails(await TemplateAPIs.getTemplate(templateId));
        } catch (error) {
            console.error('Error fetching template:', error);
        } finally {
            setIsLoading(false);
        }
    }, [templateId]);

    const updateORCreateCreative = useCallback(async () => {
        setIsLoading(true);
        try {
            if (creativeId) {
                const to_add = selectedSizes.filter((id) => !initiallySelectedSizes?.includes(id));
                const to_remove = removedSizes.filter((id) => !selectedSizes.includes(id));
                await CreativeAPIs.updateCreative(creativeId, {
                    creative_variants: {
                        to_add: to_add,
                        to_remove: to_remove,
                    },
                });
                onDone?.(creativeId);
                setIsOpen(false);
            } else {
                const creative = await CreativeAPIs.createCreative({
                    name: 'Untitled Creative',
                    template: templateId,
                    template_version: templateDetails?.publishedVersion?.id ?? 1,
                    creative_variants: selectedSizes.map((id) => ({ variant: id })),
                });
                onDone?.(creative.id);
                setIsOpen(false);
            }
        } catch (error) {
            console.error('Error updating/creating creative:', error);
        } finally {
            setSelectedSizes([]);
            setIsLoading(false);
        }
    }, [
        creativeId,
        templateId,
        onDone,
        selectedSizes,
        templateDetails,
        initiallySelectedSizes,
        removedSizes,
        setIsOpen,
    ]);

    const updateSizes = (variant: TemplateVariant) => {
        setSelectedSizes((prev) =>
            prev.includes(variant.id) ? prev.filter((id) => id !== variant.id) : [...prev, variant.id],
        );
        setRemovedSizes((prev) =>
            initiallySelectedSizes?.includes(variant.id)
                ? [...prev, variant.id]
                : prev.filter((id) => id !== variant.id),
        );
    };

    useEffect(() => {
        if (initiallySelectedSizes) {
            setSelectedSizes(initiallySelectedSizes);
        }
    }, [initiallySelectedSizes]);

    useEffect(() => {
        if (isOpen) {
            fetchTemplateDetails();
        } else {
            setTemplateDetails(null);
            setSelectedSizes([]);
            setRemovedSizes([]);
        }
    }, [isOpen, fetchTemplateDetails]);

    return (
        <Modal isOpen={isOpen}>
            <div className="flex flex-col absolute min-w-96 w-[45vw] max-w-[900px] bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out">
                <div className="flex flex-row justify-between p-4 items-center">
                    <p className="text-white text-xl">Select sizes for your creative</p>
                    <XMarkIcon
                        className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                        onClick={isLoading ? () => {} : () => setIsOpen(false)}
                    />
                </div>
                <hr className="bg-gray-100 border-gray-100" />
                <div className="h-[50vh] overflow-y-scroll no-scrollbar p-4">
                    <div className="grid grid-cols-3 gap-y-5 gap-x-5">
                        {templateDetails?.publishedVersion?.variants.map((variant) => (
                            <SizeTile
                                key={variant.id}
                                isSelected={selectedSizes.includes(variant.id)}
                                title={variant.name}
                                thumbnail={variant.thumbnail}
                                size={`${variant.svgFile.width}x${variant.svgFile.height}`}
                                onClick={() => updateSizes(variant)}
                            />
                        ))}
                    </div>
                </div>
                <hr className="bg-gray-100 border-gray-100" />
                <div className="w-full flex flex-row justify-end gap-3 p-4">
                    <Button
                        title="Cancel"
                        className="hover:bg-gray-160"
                        buttonType={ButtonType.SECONDARY}
                        buttonSize={ButtonSize.LARGE}
                        disabled={isLoading}
                        onClick={() => {
                            setIsOpen(false);
                        }}
                    />
                    <Button
                        title="Done"
                        buttonType={ButtonType.PRIMARY}
                        buttonSize={ButtonSize.LARGE}
                        disabled={isLoading || selectedSizes.length === 0}
                        onClick={updateORCreateCreative}
                    />
                </div>
            </div>
        </Modal>
    );
};
